import React from 'react'
import { Token } from '@/types/token.type'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Card, CardContent } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

type TransferType = 'user-to-user' | 'system-topup'

interface TransferHeaderCardProps {
  transferType: TransferType
  onTransferTypeChange: (type: TransferType) => void
  tokens: Token[]
  selectedToken: string
  onTokenSelect: (tokenId: string) => void
}

export default function TransferHeaderCard({
  transferType,
  onTransferTypeChange,
  tokens,
  selectedToken,
  onTokenSelect,
}: TransferHeaderCardProps) {
  return (
    <Card>
      <CardContent>
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-black">โอนเหรียญ RFC</h2>
          </div>

          <Separator className="border-gray-200" />

          <div className="flex gap-6 flex-wrap">
            {/* Transfer Type Selection */}
            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-500 mb-1">
                ประเภทการโอน
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {[
                  {
                    value: 'user-to-user',
                    label: 'โอนระหว่างผู้ใช้',
                    icon: 'Users',
                  },
                  {
                    value: 'system-topup',
                    label: 'เติมเงินจากระบบ',
                    icon: 'UserPlus',
                  },
                ].map((type) => (
                  <Button
                    key={type.value}
                    variant={
                      transferType === type.value ? 'default' : 'outline'
                    }
                    className="h-auto p-3 flex flex-col items-center gap-1 text-xs"
                    onClick={() =>
                      onTransferTypeChange(type.value as TransferType)
                    }
                  >
                    <span className="font-medium">{type.label}</span>
                  </Button>
                ))}
              </div>
            </div>

            {/* Token Selection */}
            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-500 mb-1">
                โทเคนที่ต้องการโอน
              </p>
              <Select value={selectedToken} onValueChange={onTokenSelect}>
                <SelectTrigger className="min-w-[200px]">
                  <SelectValue placeholder="เลือกโทเคนที่ต้องการโอน" />
                </SelectTrigger>
                <SelectContent>
                  {tokens.map((token) => (
                    <SelectItem key={token.id} value={token.id}>
                      <div className="flex items-center gap-2">
                        <Avatar className="size-5">
                          <AvatarImage
                            src={token.logoUrl}
                            alt={token.name}
                            className="object-cover"
                          />
                          <AvatarFallback className="size-5 text-xs">
                            {token.symbol.slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium text-sm">
                            {token.name}
                          </div>
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
